import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import {
  faBookOpen,
  faBooks,
  faCalendar,
  faInfoCircle,
  faMessage,
  faMegaphone,
  faSchool,
} from '@fortawesome/pro-solid-svg-icons';
import Dropdown from '@mysidewalk/react-dropdown';

import {
  CATALOG_INDEX,
  HELP_INDEX,
  HELP_DATA_UPDATES,
  LEGAL_ACCEPTABLE_USE_POLICY,
  LEGAL_PRIVACY_POLICY,
  RESOURCES_WHEREHOUSE_INDEX,
  TRAINING_INDEX,
} from './URLs.js';
import Access from './types/Access';
import Domains from './types/Domains';
import LinkHandler from './types/LinkHandler';

export type Props = {
  access: Access;
  domains: Domains;
  onClickLink: LinkHandler;
  onClickChat: (event: React.MouseEvent, close?: () => void) => void;
};

function HelpMenu({
  access,
  domains,
  onClickLink,
  onClickChat,
}: Props) {
  const resources = (
    access.hasWhereHouseAccess
      ? (
          <Dropdown.Item
            id="help-menu-wherehouse-resources"
            data-test-id="header.help-menu.links.help-wherehouse"
            href={`${domains.seek}${RESOURCES_WHEREHOUSE_INDEX}`}
            onClick={onClickLink}
          >
            <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faBookOpen} /></Dropdown.ItemIcon>
            WhereHouse resources
          </Dropdown.Item>
      )
      : null
  );
  return (
    <Dropdown variant="primary" className="ApplicationHeader-Dropdown">
      <Dropdown.Toggle
        className="ApplicationHeader-action"
        id="mysw-header-help-menu-toggle"
        title="View support options"
      >
        <FontAwesomeIcon icon={faQuestionCircle} style={{ fontSize: '20px' }} />
        <span className="sr-only">View support options</span>
      </Dropdown.Toggle>
      <Dropdown.Menu align="right">
        <Dropdown.Item
          id="help-menu-education"
          data-test-id="header.help-menu.links.education"
          href={TRAINING_INDEX}
          target="_blank"
          rel="noreferrer"
          onClick={onClickLink}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faSchool} /></Dropdown.ItemIcon>
          mySidewalk Academy
        </Dropdown.Item>
        <Dropdown.Item
          id="help-menu-chat"
          data-test-id="header.help-menu.actions.chat"
          as="button"
          onClick={onClickChat}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faMessage} /></Dropdown.ItemIcon>
          Chat with us
        </Dropdown.Item>
        {resources}
        <Dropdown.Item
          id="help-menu-knowledge-center"
          data-test-id="header.help-menu.links.help"
          href={HELP_INDEX}
          target="_blank"
          rel="noreferrer"
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faInfoCircle} /></Dropdown.ItemIcon>
          Knowledge center
        </Dropdown.Item>
        <Dropdown.Item
          id="help-menu-data-catalog"
          data-test-id="header.help-menu.links.catalog"
          href={`${domains.catalog}${CATALOG_INDEX}`}
          onClick={onClickLink}
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faBooks} /></Dropdown.ItemIcon>
          Data catalog
        </Dropdown.Item>
        <Dropdown.Item
          id="help-menu-product-updates"
          data-test-id="header.help-menu.links.product-updates"
          href={HELP_DATA_UPDATES}
          target="_blank"
          rel="noreferrer"
        >
          <Dropdown.ItemIcon><FontAwesomeIcon className="fa-fw" icon={faMegaphone} /></Dropdown.ItemIcon>
          Data updates
        </Dropdown.Item>
        <Dropdown.MenuFooter style={{ fontSize: 'var(--mysw-size-800)' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <a
              id="user-menu-privacy-policy"
              data-test-id="header.user-menu.links.privacy-policy"
              className="Dropdown-MenuFooterAction"
              href={`${domains.seek}${LEGAL_PRIVACY_POLICY}`}
              target="_blank"
              rel="noreferrer"
            >
              Privacy policy
            </a>
            <a
              id="user-menu-acceptable-use-policy"
              data-test-id="header.user-menu.links.acceptable-use-policy"
              className="Dropdown-MenuFooterAction"
              href={`${domains.seek}${LEGAL_ACCEPTABLE_USE_POLICY}`}
              target="_blank"
              rel="noreferrer"
            >
              Acceptable use policy
            </a>
          </div>
          <div style={{ marginTop: '2px' }}>
            &copy;
            {' '}
            {new Date().getFullYear()}
            {' '}
            mySidewalk Inc. All rights reserved.
          </div>
        </Dropdown.MenuFooter>
      </Dropdown.Menu>
    </Dropdown>
  );
}

HelpMenu.displayName = 'HelpMenu';

export default HelpMenu;
